import { useEffect, useState } from 'react';

/**
 * Full view hides the editor chrome (header bar, toolbar, document panel) so
 * the document gets its height back. The state lives on a body class so CSS
 * can hide the chrome, is remembered per browser, and is announced with a
 * resize so anything measuring the viewport re-measures. Ported from
 * milegrid's utils/fullView.js; the class name is milepad's existing one.
 */
export const FULL_VIEW_CLASS = 'mp-fullscreen';
export const FULL_VIEW_STORAGE_KEY = 'milepad-full-view';
const CHANGE_EVENT = 'milepad-full-view-change';

export const isFullView = () => document.body.classList.contains(FULL_VIEW_CLASS);

export const setFullView = (on) => {
  document.body.classList.toggle(FULL_VIEW_CLASS, !!on);
  try {
    localStorage.setItem(FULL_VIEW_STORAGE_KEY, on ? '1' : '0');
  } catch (e) {
    // Private mode or blocked storage: the choice just is not remembered.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
  window.dispatchEvent(new Event('resize'));
};

export const toggleFullView = () => setFullView(!isFullView());

/** Applies the remembered state; call once before the page first measures. */
export const restoreFullView = () => {
  let remembered = null;
  try {
    remembered = localStorage.getItem(FULL_VIEW_STORAGE_KEY);
  } catch (e) {
    remembered = null;
  }
  if (remembered === '1' && !isFullView()) {
    document.body.classList.add(FULL_VIEW_CLASS);
  }
};

/** Ctrl+Shift+F (Cmd+Shift+F on Mac) toggles full view, as in milegrid. */
export const useFullViewShortcut = () => {
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        toggleFullView();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
};

/** React binding: [isOn, toggle], re-rendering when it changes anywhere. */
export const useFullView = () => {
  const [on, setOn] = useState(() => {
    restoreFullView();
    return isFullView();
  });
  useEffect(() => {
    const sync = () => setOn(isFullView());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);
  return [on, toggleFullView];
};
